'use client';

import { useState, useRef, useEffect, useLayoutEffect, useCallback, useMemo } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import MessageContent from '@/components/message-content';
import RecommendedQuestions from '@/components/RecommendedQuestions';
import { FeedbackButtons } from '@/components/feedback-buttons';
import Header from '@/components/header';
import AgentSelector from '@/components/agent-selector';
import PersonaSelector from '@/components/persona-selector';
import FollowupQueries from '@/components/followup-queries';
import { hasCompleteTables } from '@/lib/markdown-utils';
import { logger } from '@/lib/logger';
import { parseFollowupQueries, type FollowupQuery } from '@/lib/followup-parser';
import { getErrorMessage } from '@/lib/error-messages';
import { getUserTimezone } from '@/lib/timezone';
import { buildSessionId, normalizeUserId } from '@/lib/user-session-rules';
import { getFeatureRestrictionFromResponse, normalizeUserLevel, type FeatureRestriction } from '@/lib/feature-permissions';
import { normalizeSessionMessages } from '@/lib/session-message-normalizer';
import { getHistorySessionUserId } from '@/lib/session-history-access';
import type { LLMErrorResponse } from '@/types/error';
import {
  advanceAgentPhase,
  getToolStatusTranslationKey,
  isStreamCompletionEvent,
  normalizeChatStreamEventData,
  normalizeStreamErrorCode,
  type AgentPhase,
} from '@/lib/chat-stream-events';
import { useLanguage } from '@/contexts/language-context';
import { useSessionContext } from '@/contexts/session-context';
import { useSidebar } from '@/contexts/sidebar-context';
import { useNotifications } from '@/contexts/notification-context';
import { CopyButton } from '@/components/copy-button';
import { generateVantageDeeplink, mapToVantageSymbol } from '@/lib/vantage-assets';
import { ArrowDown, Send } from 'lucide-react';
import { useAuth } from '@/contexts/auth-context';
import { getUseChatLimit } from '@/lib/get-use-chat-limit';
import { toast } from 'sonner';
import { getNewSessionRestriction, getSessionLimit } from '@/lib/session-permissions';
import { SessionAccessDialog } from '@/components/session-access-dialog';
import { FeatureAccessDialog } from '@/components/feature-access-dialog';
import type { AgentType } from '@/lib/agent-types';
import { useDeepDataAccess } from '@/hooks/use-deep-data-access';
import { useDatapacks } from '@/contexts/DatapackContext';
import { AgentStatusIndicator } from '@/components/agent-status-indicator';
import { getFeatureRestriction, normalizeUserAccess } from '@/lib/user-access';
import MtAccountSelector from '@/components/mt-account-selector';
import { normalizeMtAccounts } from '@/lib/mt-accounts';
import { useLoginDialog } from '@/components/login-dialog';
import { MembershipTiersDialog } from '@/components/membership-tiers-dialog';

interface Message {
  id: string;
  content: string;
  role: 'user' | 'assistant';
  timestamp: Date;
  isStreaming?: boolean;
  hasBeenParsed?: boolean;
  traceId?: string | null;
  feedbackStatus?: 'positive' | 'negative' | null;
  tradeRecommendations?: Array<{
    asset: string;
    deeplink: string;
    price?: string;
  }>;
  followupsHidden?: boolean;
  followupQueries?: FollowupQuery[];
}

const MAX_CHARS = 2000;
const REFUSAL_MESSAGE =
  "I'm specifically designed to help with trading and investing-related tasks on the Vantage trading platform. I'm not able to assist with your request, but I'd be happy to help if you have questions related to trading and investing.";

function getFeatureRestrictionMessage(restriction: FeatureRestriction, language: string): string {
  if (restriction === 'login') {
    return language === 'zh' ? '请先登录/注册后使用此功能。' : 'Please sign in to use this feature.';
  }

  if (restriction === 'daily-limit') {
    return language === 'zh' ? '今日深度复盘次数已用完。' : 'Daily deep review limit reached.';
  }

  return language === 'zh' ? '此功能为挑战用户/VIP专属。' : 'This feature is available for Challenge/VIP users only.';
}

interface ChatProps {
  sessionId?: string; // Optional: undefined = new chat, string = load existing session
  notificationId?: string; // Optional: set when opening from a notification
}

class SessionLimitReachedError extends Error {
  constructor() {
    super('Session limit reached');
    this.name = 'SessionLimitReachedError';
  }
}

export default function Chat({ sessionId: initialSessionId, notificationId }: ChatProps = {}) {
  const pathname = usePathname();
  const router = useRouter();
  const { t, isRTL, language } = useLanguage();
  const { sessions, sessionStatus, addSession } = useSessionContext();
  const { isOpen: isSidebarOpen } = useSidebar();
  const { activeNotification, setActiveNotification, getNotificationIdForSession, linkSessionToNotification, markAsRead } = useNotifications();
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingSession, setIsLoadingSession] = useState(false);
  const [sessionId, setSessionId] = useState<string>('');
  const [questionsKey, setQuestionsKey] = useState(0);
  const [selectedAgent, setSelectedAgent] = useState<AgentType>(null);
  const [selectedMtAccount, setSelectedMtAccount] = useState<string | null>(null);
  const [membershipDialogOpen, setMembershipDialogOpen] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const isNewSessionRef = useRef(!initialSessionId);
  const [abortController, setAbortController] = useState<AbortController | null>(null);
  const [currentStreamId, setCurrentStreamId] = useState<string | null>(null);
  const userCancelledRef = useRef(false); // Track if user clicked stop button
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [streamingPhase, setStreamingPhase] = useState<AgentPhase>('thinking');
  const [streamingLabel, setStreamingLabel] = useState<string | null>(null);
  const { setPrefetchedDatapacks, clearDatapacks } = useDatapacks();
  const { user, remaining, setRemaining, loading: authLoading, isLogin } = useAuth();
  const { openLoginDialog } = useLoginDialog();
  const userLevel = normalizeUserLevel(user?.level ?? 'L0');
  const userAccess = useMemo(() => normalizeUserAccess(user), [user]);
  const mtAccounts = useMemo(() => (isLogin && (userLevel === 'L2' || userLevel === 'L3') ? normalizeMtAccounts(user?.mt_account) : []), [isLogin, user?.mt_account, userLevel]);
  const activeMtAccount = selectedMtAccount && mtAccounts.includes(selectedMtAccount) ? selectedMtAccount : null;
  const klineRestriction = getFeatureRestriction(userAccess.features.kline_chart, isLogin);
  const currentUserId = normalizeUserId(user?.userId);
  const historySessionUserId = useMemo(() => getHistorySessionUserId(initialSessionId, currentUserId), [initialSessionId, currentUserId]);
  const [sessionDialog, setSessionDialog] = useState<{ open: boolean; variant: 'login' | 'limit'; limit?: number }>({ open: false, variant: 'limit' });
  const [chatUsageDialogOpen, setChatUsageDialogOpen] = useState(false);
  const [recommendationDialog, setRecommendationDialog] = useState<{ open: boolean; restriction: FeatureRestriction }>({ open: false, restriction: 'upgrade' });
  const [klineDialogOpen, setKlineDialogOpen] = useState(false);
  const {
    agentRestrictions,
    featureDialog,
    guardAgentAccess,
    handleRestrictedAgentClick,
    isAgentRestricted,
    markAgentUsed,
    setFeatureDialogOpen,
    showFeatureRestrictionForErrorCode,
  } = useDeepDataAccess({
    authLoading,
    userId: user?.userId,
    userLevel,
  });
  const userScrolledAwayRef = useRef(false); // Track if user manually scrolled away during current stream
  const lastScrollTopRef = useRef(0); // Track last scroll position to detect scroll direction
  const isStreamingRef = useRef(false); // Track streaming state for scroll handler (avoids listener churn)
  // 用户每日使用次数
  const [remainingDay, setRemainingDay] = useState<number>(3);

  useEffect(() => {
    setSelectedMtAccount((current) => (current && mtAccounts.includes(current) ? current : null));
  }, [mtAccounts]);

  const handleUnavailableMtAccountClick = useCallback(() => {
    if (!isLogin) {
      openLoginDialog();
      return;
    }

    setMembershipDialogOpen(true);
  }, [isLogin, openLoginDialog]);

  const createClientSessionId = useCallback((rawUserId: unknown): string => {
    const nextSessionId = buildSessionId(rawUserId);
    if (!nextSessionId) {
      logger.error('[Chat] Failed to build session ID for user', { userId: rawUserId });
      return '';
    }

    return nextSessionId;
  }, []);

  const handleLockedKlineClick = useCallback(() => {
    if (klineRestriction) {
      setKlineDialogOpen(true);
    }
  }, [klineRestriction]);

  // Check onboarding status on mount
  // useEffect(() => {
  //   if (!_user) return;

  //   // Clean up legacy global key (pre-user-scoped fix)
  //   localStorage.removeItem('onboarding_dismissed');

  //   const checkOnboarding = async () => {
  //     try {
  //       const headers: Record<string, string> = {};
  //       const savedUrl = getSelectedBackendUrl();
  //       if (isDevelopmentDeploy() && savedUrl) {
  //         headers[BACKEND_URL_HEADER] = savedUrl;
  //       }

  //       const response = await fetch('/api/onboarding/status', { headers });
  //       if (response.ok) {
  //         const data = await response.json();
  //         if (!data.onboarding_completed && !data.onboarding_dismissed && !localStorage.getItem(`onboarding_dismissed_${_user.sub}`)) {
  //           setShowOnboarding(true);
  //         }
  //       }
  //     } catch {
  //       // Never block chat on onboarding check failure
  //     }
  //   };

  //   checkOnboarding();
  // }, [_user]);

  // Notification awareness: determine if this chat is tied to a notification
  const [currentNotificationId, setCurrentNotificationId] = useState<string | null>(notificationId || null);

  const resolveRefusalMessage = (message?: string): string | undefined => {
    if (!message) return undefined;
    return message === REFUSAL_MESSAGE ? t('errors.refusal') : undefined;
  };

  const scrollToBottom = (instant = false) => {
    messagesEndRef.current?.scrollIntoView({ behavior: instant ? 'auto' : 'smooth' });
    userScrolledAwayRef.current = false; // User wants to see bottom again
  };

  useLayoutEffect(() => {
    // Scroll when loading completes (isLoadingSession becomes false)
    // This ensures messages are rendered in DOM before scrolling
    if (!isLoadingSession && messages.length > 0) {
      // Compute streaming state directly from messages to avoid timing issues
      // (useEffect that updates isStreamingRef runs after useLayoutEffect)
      const isCurrentlyStreaming = messages[messages.length - 1]?.isStreaming ?? false;

      // Only auto-scroll during active streaming if user hasn't scrolled away
      // Past sessions: no forced scroll, let it land naturally
      const shouldAutoScroll = isCurrentlyStreaming && !userScrolledAwayRef.current;

      if (shouldAutoScroll) {
        scrollToBottom(true); // Instant scroll during streaming (prevents animation pile-up)
      }
    }
  }, [isLoadingSession, messages]);

  // Generate session ID only if not provided via prop
  // Also clear messages when starting a new chat (no initialSessionId)
  useEffect(() => {
    if (initialSessionId) {
      setSessionId(initialSessionId);
      setIsLoadingSession(true); // Show loading for existing session
    } else {
      setMessages([]);
      if (user?.userId) {
        setSessionId(createClientSessionId(user.userId));
      } else {
        setSessionId('');
      }
      setIsLoadingSession(false); // New chat - no loading needed
    }
  }, [initialSessionId, user?.userId, createClientSessionId]);

  // Clear messages when navigating to homepage (New Chat button clicked)
  useEffect(() => {
    if (pathname === '/' && !initialSessionId) {
      setMessages([]);
      if (user?.userId) {
        setSessionId(createClientSessionId(user.userId));
      } else {
        setSessionId('');
      }
    }
  }, [pathname, initialSessionId, user?.userId, createClientSessionId]);

  // Notification: detect return visits by checking if session is linked to a notification
  useEffect(() => {
    if (!currentNotificationId && initialSessionId) {
      const linkedNotificationId = getNotificationIdForSession(initialSessionId);
      if (linkedNotificationId) {
        setCurrentNotificationId(linkedNotificationId);
      }
    }
  }, [initialSessionId, currentNotificationId, getNotificationIdForSession]);

  // Notification: inject notification content as first assistant message when opening from notification
  useEffect(() => {
    if (notificationId && activeNotification && activeNotification.notificationId === notificationId && messages.length === 0) {
      setMessages([
        {
          id: `notification-${notificationId}`,
          content: activeNotification.content,
          role: 'assistant',
          timestamp: new Date(),
          hasBeenParsed: true,
        },
      ]);
      // Clear activeNotification after injecting so it's not re-injected
      setActiveNotification(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notificationId, activeNotification]);

  // Notification: recover content on hard refresh when activeNotification is lost
  useEffect(() => {
    if (!notificationId || activeNotification || messages.length > 0) return;

    let cancelled = false;
    setIsLoading(true);

    async function recoverNotification() {
      try {
        const encodedId = encodeURIComponent(notificationId!);
        const normalized = normalizeUserId(user?.userId);
        const query = normalized ? `?userId=${encodeURIComponent(normalized)}` : '';
        const res = await fetch(`/api/notifications/${encodedId}${query}`);

        if (cancelled) return;

        if (!res.ok) {
          const restriction = await getFeatureRestrictionFromResponse(res);
          if (cancelled) return;

          if (restriction) {
            setRecommendationDialog({ open: true, restriction });
            return;
          }

          logger.error('[Chat] Failed to recover notification:', res.status);
          return;
        }

        const data = await res.json();
        if (cancelled) return;

        // First-open: inject content
        if (data.content) {
          setMessages([
            {
              id: `notification-${notificationId}`,
              content: data.content,
              role: 'assistant',
              timestamp: new Date(),
              hasBeenParsed: true,
            },
          ]);
        }
        markAsRead(notificationId!);
      } catch (error) {
        logger.error('[Chat] Error recovering notification:', error);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    recoverNotification();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notificationId, user?.userId]);

  // Load messages when an existing session is opened
  useEffect(() => {
    async function loadSessionMessages() {
      if (!initialSessionId) return; // Skip if this is a new chat
      if (authLoading) return;

      // Clear old datapacks when switching to a new session
      clearDatapacks();
      const userId = historySessionUserId || '';
      if (!userId) return;

      try {
        const response = await fetch(`/api/sessions/${initialSessionId}/messages?userId=${userId}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          // Handle authentication errors by redirecting to login
          if (response.status === 401) {
            logger.warn('[Chat] Authentication failed - redirecting to login');
            // window.location.href = '/api/auth/login';
            return;
          }
          throw new Error(`Failed to load session messages: ${response.status}`);
        }

        const data = await response.json();
        const loadedMessages: Message[] = normalizeSessionMessages(data);

        setMessages(loadedMessages);
        logger.info(`[Chat] Loaded ${loadedMessages.length} messages from session ${initialSessionId}`);

        // Prefetch datapacks for historical sessions (Phase 1)
        // Extract all datapack IDs from chart tags in messages
        if (userLevel === 'L0') {
          logger.info('[Chat] Skipping chart datapack prefetch for L0 user');
        } else {
          const datapackIds = new Set<string>();
          loadedMessages.forEach((msg) => {
            // Match: <chart ... data-chart-id="uuid" /> or data-chart-data-ids="uuid1,uuid2,..."
            const chartIdMatches = msg.content.matchAll(/data-chart-id="([^"]+)"/g);
            for (const match of chartIdMatches) {
              if (match[1]) datapackIds.add(match[1]);
            }

            // Match comparison charts with multiple dataIds
            const chartDataIdsMatches = msg.content.matchAll(/data-chart-data-ids="([^"]+)"/g);
            for (const match of chartDataIdsMatches) {
              if (match[1]) {
                // Split comma-separated IDs
                match[1].split(',').forEach((id) => datapackIds.add(id.trim()));
              }
            }

            // Match legacy format: data_id (underscore instead of hyphen)
            const dataIdMatches = msg.content.matchAll(/data_id="([^"]+)"/g);
            for (const match of dataIdMatches) {
              if (match[1]) datapackIds.add(match[1]);
            }
          });

          const datapackIdsArray = Array.from(datapackIds);
          if (datapackIdsArray.length > 0) {
            logger.info(`[Chat] Prefetching ${datapackIdsArray.length} datapacks for historical session`);

            // Call batch API and WAIT for completion before rendering charts
            // This ensures charts have prefetched data available when they render
            try {
              const response = await fetch('/api/datapacks/batch', {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                  datapack_ids: datapackIdsArray,
                  session_id: initialSessionId,
                }),
              });

              if (response.ok) {
                const data = await response.json();
                if (Array.isArray(data.datapacks)) {
                  setPrefetchedDatapacks(data.datapacks);
                }
                logger.info(`[Chat] 📦 Batch API response:`, data);
                logger.info(`[Chat] 📦 Datapacks in response:`, data.datapacks);
                logger.info(`[Chat] ✅ Prefetch complete: ${datapackIdsArray.length} datapacks stored in context`);
              } else {
                logger.warn(`[Chat] Prefetch failed with status ${response.status}`);
              }
            } catch (err) {
              logger.warn('[Chat] Prefetch error (non-blocking):', err);
            }
          } else {
            logger.info('[Chat] No datapacks to prefetch (no charts in messages)');
          }
        }
      } catch (error) {
        logger.error('[Chat] Error loading session messages:', error);
      } finally {
        setIsLoadingSession(false); // Always clear loading state
      }
    }

    loadSessionMessages();
  }, [initialSessionId, authLoading, router, historySessionUserId, userLevel, clearDatapacks, setPrefetchedDatapacks]);

  useEffect(() => {
    if (messages.length === 0) {
      setQuestionsKey((prev) => prev + 1);
    }
  }, [messages.length]);

  useEffect(() => {
    setRemainingDay(getUseChatLimit(user?.level, user?.remaining_days));
  }, [user?.level, user?.remaining_days]);

  useEffect(() => {
    if (isAgentRestricted(selectedAgent)) {
      setSelectedAgent(null);
    }
  }, [isAgentRestricted, selectedAgent]);

  // Cleanup: abort any in-progress request when component unmounts
  useEffect(() => {
    return () => {
      if (abortController) {
        abortController.abort();
      }
    };
  }, [abortController]);

  // Keep streaming state ref updated for scroll handler
  useEffect(() => {
    isStreamingRef.current = messages.length > 0 && (messages[messages.length - 1]?.isStreaming ?? false);
  }, [messages]);

  // Detect scroll position to show/hide jump to bottom button
  // Also detect if user manually scrolls UP during streaming
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const scrollHeight = document.documentElement.scrollHeight;
      const clientHeight = document.documentElement.clientHeight;
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
      const shouldShow = distanceFromBottom > 100; // Show button if scrolled up more than 100px
      setShowScrollButton(shouldShow);

      // Detect UPWARD scroll during streaming (user scrolling away from bottom)
      const isCurrentlyStreaming = isStreamingRef.current;
      if (isCurrentlyStreaming) {
        const scrolledUp = scrollTop < lastScrollTopRef.current;
        if (scrolledUp) {
          userScrolledAwayRef.current = true;
        }
      }

      lastScrollTopRef.current = scrollTop;
    };

    // Initial check
    handleScroll();

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []); // Attach listener once on mount

  const sendMessage = async (messageToSend?: string) => {
    const messageContent = messageToSend || input;
    if (!messageContent.trim()) return;

    if (remaining <= 0) {
      setChatUsageDialogOpen(true);
      return;
    }

    const userId = currentUserId;
    if (!userId) {
      toast.error('Initializing user information; please try again later.', { position: 'top-center' });
      return;
    }

    let sessionIdToUse = sessionId;
    if (!sessionIdToUse || !sessionIdToUse.startsWith(`${userId}-`)) {
      sessionIdToUse = createClientSessionId(userId);
    }

    if (!sessionIdToUse) {
      toast.error('Unable to create a valid session ID. Please try again.', { position: 'top-center' });
      return;
    }

    // Newness belongs to this Chat instance, not the sidebar's visible session
    // list. The list can lag behind PHP persistence, and two root tabs can briefly
    // generate the same timestamp-based ID.
    const isCreatingNewSession = isNewSessionRef.current;
    if (isCreatingNewSession) {
      const sessionRestriction = getNewSessionRestriction(userAccess, sessions.length, sessionStatus);

      if (sessionRestriction === 'loading' || sessionRestriction === 'unavailable') {
        toast(sessionRestriction === 'loading' ? t('common.loading') : t('common.error'), { position: 'top-center' });
        return;
      }

      if (sessionRestriction === 'limit') {
        setSessionDialog({
          open: true,
          variant: 'limit',
          limit: getSessionLimit(userAccess),
        });
        return;
      }
    }

    const agentForRequest = selectedAgent;
    if (!guardAgentAccess(agentForRequest)) {
      return;
    }

    if (initialSessionId && sessionIdToUse !== initialSessionId) {
      router.replace(`/chat/${sessionIdToUse}`);
      toast.error('Session is no longer available. Starting a new chat.', { position: 'top-center' });
      return;
    }

    if (sessionIdToUse !== sessionId) {
      setSessionId(sessionIdToUse);
    }

    // Reset cancellation flag and auto-scroll preference
    userCancelledRef.current = false;
    userScrolledAwayRef.current = false; // Resume auto-scroll for new message

    const firstUserMessage = messageContent;

    const userMessage: Message = {
      id: Date.now().toString(),
      content: messageContent,
      role: 'user',
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);
    focusTextarea();

    const assistantMessageId = (Date.now() + 1).toString();
    const assistantMessage: Message = {
      id: assistantMessageId,
      content: '',
      role: 'assistant',
      timestamp: new Date(),
      isStreaming: true,
    };

    setMessages((prev) => [...prev, assistantMessage]);

    // Frontend-observable state machine: request pending → stream open → stream active.
    // Legacy phase events can still advance the same state machine when available.
    let currentPhase: AgentPhase = 'thinking';
    let phaseTimeoutId: NodeJS.Timeout | undefined;
    setStreamingPhase('thinking');
    setStreamingLabel(null);

    const updatePhase = (nextPhase: AgentPhase) => {
      const advancedPhase = advanceAgentPhase(currentPhase, nextPhase);
      if (advancedPhase === currentPhase && nextPhase !== currentPhase) return;

      currentPhase = advancedPhase;
      setStreamingPhase(advancedPhase);

      if (phaseTimeoutId && advancedPhase !== 'planning') {
        clearTimeout(phaseTimeoutId);
        phaseTimeoutId = undefined;
      }
    };

    const startPlanningPhase = () => {
      updatePhase('planning');
      if (currentPhase !== 'planning' || phaseTimeoutId) return;

      phaseTimeoutId = setTimeout(() => {
        logger.info('[Chat] Planning timeout - transitioning to working');
        updatePhase('working');
      }, 10000);
    };

    // Declare timeoutId outside try block so it's accessible in finally
    let timeoutId: NodeJS.Timeout | undefined;

    try {
      // Create AbortController for both user cancellation and timeout
      const controller = new AbortController();
      setAbortController(controller); // Store for stop button
      timeoutId = setTimeout(() => controller.abort(), 180000); // 180 seconds

      // Generate stream ID client-side to support early cancellation
      // 原写法
      const newStreamId = typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
      // 现在写法，保证控制台不报出错误
      // const newStreamId = typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `${Date.now()}}`;
      setCurrentStreamId(newStreamId);

      const streamRequestBody = {
        user_id: userId,
        session_id: sessionIdToUse,
        stream_id: newStreamId,
        message: messageContent,
        model: agentForRequest || undefined,
        is_new_session: isCreatingNewSession,
        notification_id: currentNotificationId || undefined,
        timezone: getUserTimezone(),
        ...(activeMtAccount ? { mt_id: [Number(activeMtAccount)] } : {}),
      };

      const response = await fetch(`/api/stream`, {
        method: 'POST',
        body: JSON.stringify(streamRequestBody),
        signal: controller.signal,
      });
      if (!response.ok) {
        // Handle authentication errors by redirecting to login
        if (response.status === 401) {
          const res = await response.json();
          logger.warn('[Chat] Authentication failed - redirecting to login');
          // toast.error(res.error, { position: 'top-center' });
          throw new Error(res.error || 'Sorry, there was an error connecting to the server.');
          // window.location.href = '/api/auth/login';
        }

        // Handle notification session mismatch by redirecting to the correct session
        if (response.status === 409) {
          let sessionLimit: number | null = null;
          try {
            const errorBody = await response.json();
            if (errorBody?.error?.code === 'session_limit_reached') {
              sessionLimit = typeof errorBody.error.limit === 'number' ? errorBody.error.limit : getSessionLimit(userAccess);
            } else if (errorBody?.error?.linked_session_id) {
              logger.warn('[Chat] Notification session mismatch, redirecting to linked session');
              if (currentNotificationId) {
                linkSessionToNotification(currentNotificationId, errorBody.error.linked_session_id);
              }
              router.replace(`/chat/${errorBody.error.linked_session_id}`);
              return;
            }
            if (sessionLimit === null) {
              // 409 without redirect target — log details and show generic error
              logger.error('[Chat] Conflict error:', errorBody?.error);
            }
          } catch {}

          if (sessionLimit !== null) {
            setSessionDialog({ open: true, variant: 'limit', limit: sessionLimit });
            throw new SessionLimitReachedError();
          }

          throw new Error('Sorry, there was an error connecting to the server.');
        }

        // Try to parse structured LLM error response
        const contentType = response.headers.get('Content-Type') || '';
        if (contentType.includes('application/json')) {
          let errorBody: LLMErrorResponse | null = null;
          try {
            errorBody = await response.json();
          } catch (jsonError) {
            // If JSON parsing fails, fall through to generic error
            logger.warn('[Chat] Failed to parse error response:', jsonError);
          }

          // If we successfully parsed the JSON, check for structured errors
          if (errorBody?.error?.code) {
            if (errorBody.error.code === 'session_limit_reached') {
              const limit = typeof errorBody.error.limit === 'number' ? errorBody.error.limit : getSessionLimit(userAccess);
              setSessionDialog({
                open: true,
                variant: 'limit',
                limit,
              });
              throw new SessionLimitReachedError();
            }

            const featureRestriction = showFeatureRestrictionForErrorCode(errorBody.error.code);
            if (featureRestriction) {
              throw new Error(getFeatureRestrictionMessage(featureRestriction, language));
            }

            // Structured error with code: log backend details, show user-friendly message
            logger.error('[Chat] LLM error:', {
              code: errorBody.error.code,
              message: errorBody.error.message,
              retry_after: errorBody.error.retry_after,
              details: errorBody.error.details,
            });

            const errorMsg = resolveRefusalMessage(errorBody.error.message) ?? getErrorMessage(errorBody.error.code, errorBody.error.message);
            throw new Error(errorMsg);
          } else if (errorBody?.error?.message) {
            // Unstructured error: log backend message, show generic to user
            logger.error('[Chat] Backend error (no code):', errorBody.error.message);
            throw new Error('Sorry, there was an error connecting to the server.');
          }
        }

        // Fallback to generic error (log technical details, show user-friendly message)
        logger.error(`[Chat] HTTP error! status: ${response.status}`);
        throw new Error('Sorry, there was an error connecting to the server.');
      }

      // PHP has accepted/created this session. Further messages from the same Chat
      // instance are continuations even if the sidebar list has not refreshed yet.
      isNewSessionRef.current = false;

      // The documented API does not emit phase events. Treat a successfully opened
      // response stream as the frontend-observable planning phase.
      startPlanningPhase();
      markAgentUsed(agentForRequest);

      const traceId = response.headers.get('X-Langfuse-Trace-Id');
      if (!traceId) {
        logger.warn('No X-Langfuse-Trace-Id header received for message');
      }

      // Optional sanity check: backend should echo X-Stream-ID
      const responseStreamId = response.headers.get('X-Stream-ID');
      if (responseStreamId && responseStreamId !== newStreamId) {
        logger.warn('[Chat] X-Stream-ID mismatch between client and server', {
          client: newStreamId,
          server: responseStreamId,
        });
      }

      // 每调用一次，剩余次数减1
      // 先获取当前剩余次数，再设置新值，避免函数更新报错
      setRemaining((prev) => Math.max(0, prev - 1));

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();

      if (reader) {
        let accumulatedContent = '';
        let buffer = ''; // Buffer for incomplete SSE lines
        // Maintain event state across streaming chunks so split events are parsed correctly
        let currentEvent: string | null = null;
        let currentData: string | null = null;
        let streamErrored = false;
        let streamCompleted = false; // Flag to stop loop when complete event received

        // Check if backend is sending SSE format
        const contentType = response.headers.get('Content-Type') || '';
        const isSSE = contentType.includes('text/event-stream');

        logger.info(`[Chat] Stream format: ${isSSE ? 'SSE' : 'plain text'}`);

        // STREAMING PHASE: Parse SSE events or plain text chunks
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value, { stream: true });

          if (isSSE) {
            // SSE Parsing: Buffer lines and parse events
            buffer += chunk;
            // Normalize CRLF to LF (SSE spec allows both \n and \r\n as line terminators)
            const normalizedBuffer = buffer.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
            const lines = normalizedBuffer.split('\n');

            // Keep last incomplete line in buffer
            buffer = lines.pop() || '';

            for (const line of lines) {
              if (line.startsWith('event:')) {
                currentEvent = line.substring(6).trim();
              } else if (line.startsWith('data:')) {
                currentData = line.substring(5).trim();
              } else if (line === '' && currentEvent && currentData) {
                // Complete event received (event + data + empty line)
                try {
                  const parsedEventData: unknown = JSON.parse(currentData);
                  const { eventData, payload: eventPayload } = normalizeChatStreamEventData(parsedEventData);

                  // Handle different event types
                  if (currentEvent === 'text') {
                    const textContent = eventPayload.content || '';
                    if (!textContent) {
                      logger.warn('[Chat] Text event has no content:', eventData);
                    }
                    accumulatedContent += textContent;

                    // Force transition to "working" on first text event
                    updatePhase('working');
                    setStreamingLabel(null);

                    // Debug: Log if chunk contains trade tag
                    if (textContent.includes('<trade')) {
                      logger.info('[Chat] 🚨 TRADE TAG DETECTED IN TEXT EVENT:', textContent);
                    }

                    // Strip ONLY COMPLETE trade tags during streaming
                    const displayContent = accumulatedContent
                      .replace(/<trade\s+asset="[^"]+"\s+price="[^"]+"\s*\/>/g, '') // With price
                      .replace(/<trade\s+asset="[^"]+"\s*\/>/g, ''); // Without price

                    // Show content in real-time (without trade tags)
                    setMessages((prev) => prev.map((msg) => (msg.id === assistantMessageId ? { ...msg, content: displayContent } : msg)));

                    checkForProgressiveParsing(assistantMessageId, accumulatedContent);
                  } else if (currentEvent === 'phase') {
                    // Backward compatibility for backend versions that emit phases.
                    const agentId = eventData.agent_id || eventPayload.agent_id;
                    const stage = eventPayload.stage;

                    if (!agentId || !stage) {
                      logger.warn('[Chat] Phase event missing agent_id or stage:', eventData);
                    } else {
                      logger.info(`[Chat] Phase event: ${agentId} ${stage}`);

                      if (agentId === 'planner' && stage === 'start') {
                        startPlanningPhase();
                      } else if (agentId === 'planner' && stage === 'complete') {
                        updatePhase('working');
                      }
                    }
                  } else if (isStreamCompletionEvent(currentEvent)) {
                    logger.info(`[Chat] Stream ${currentEvent} event received`, {
                      sessionId: eventPayload.session_id,
                    });
                    streamCompleted = true; // Stop reading further chunks
                  } else if (currentEvent === 'error') {
                    logger.error('[Chat] Error event received:', eventData);

                    const errorCode = normalizeStreamErrorCode(eventPayload.code);
                    const errorMessage = eventPayload.message;
                    const errorReason = eventPayload.reason || eventData.reason;

                    // Get user-friendly error message
                    let displayMessage: string;
                    if (errorCode) {
                      // Structured error: log backend details, get user-friendly message
                      logger.error('[Chat] LLM error:', {
                        code: errorCode,
                        message: errorMessage,
                        retry_after: eventPayload.retry_after,
                        details: eventPayload.details,
                      });
                      displayMessage = resolveRefusalMessage(errorMessage) ?? getErrorMessage(errorCode, errorMessage);
                    } else {
                      // Unstructured error: log backend message, show generic to user
                      logger.error('[Chat] Backend error (no code):', errorMessage || errorReason);
                      displayMessage = 'Sorry, there was an error connecting to the server.';
                    }

                    streamErrored = true;

                    // Update UI with error message and stop streaming
                    setMessages((prev) =>
                      prev.map((msg) => {
                        if (msg.id !== assistantMessageId) return msg;
                        const newContent = msg.content ? `${msg.content}\n\n${displayMessage}` : displayMessage;
                        return {
                          ...msg,
                          content: newContent,
                          isStreaming: false,
                          hasBeenParsed: true,
                        };
                      }),
                    );
                  } else if (currentEvent === 'tool_use') {
                    updatePhase('working');

                    const toolName = eventPayload.tool_name;
                    const statusMessage = eventPayload.status_message;
                    const stableTranslationKey = getToolStatusTranslationKey(toolName);
                    let displayMessage: string | undefined;

                    if (stableTranslationKey) {
                      displayMessage = t(`toolStatus.${stableTranslationKey}`);
                    } else if (statusMessage) {
                      const translatedMessage = t(`toolStatus.${statusMessage}`);
                      displayMessage = translatedMessage.startsWith('toolStatus.') ? statusMessage : translatedMessage;
                    }

                    if (displayMessage) {
                      logger.info(`[Chat] Tool use event: ${displayMessage}`);
                      setStreamingLabel(displayMessage);
                    } else {
                      setStreamingLabel(null);
                      logger.warn('[Chat] Tool use event missing a recognized status:', eventData);
                    }
                  } else {
                    // Log unknown event types for debugging (reasoning, etc.)
                    logger.info(`[Chat] Unknown event type: ${currentEvent}`, eventData);
                  }
                } catch (parseError) {
                  logger.error('[Chat] Failed to parse SSE event data:', parseError, currentData);
                }

                // Reset for next event
                currentEvent = null;
                currentData = null;
              }
            }
            if (streamErrored || streamCompleted) {
              // Stop reading further chunks once an error or complete event is handled
              break;
            }
          } else {
            // Plain text streaming (backward compatibility)
            accumulatedContent += chunk;
            updatePhase('working');
            setStreamingLabel(null);

            // Debug: Log if chunk contains trade tag
            if (chunk.includes('<trade')) {
              logger.info('[Chat] 🚨 TRADE TAG DETECTED IN CHUNK:', chunk);
            }

            // Strip ONLY COMPLETE trade tags during streaming
            const displayContent = accumulatedContent
              .replace(/<trade\s+asset="[^"]+"\s+price="[^"]+"\s*\/>/g, '') // With price
              .replace(/<trade\s+asset="[^"]+"\s*\/>/g, ''); // Without price

            // Show content in real-time (without trade tags)
            setMessages((prev) => prev.map((msg) => (msg.id === assistantMessageId ? { ...msg, content: displayContent } : msg)));

            checkForProgressiveParsing(assistantMessageId, accumulatedContent);
          }
        }

        if (streamErrored) {
          // Skip post-stream parsing when an error event was received
          return;
        }

        // POST-STREAMING PHASE: Parse trade tags and followup queries
        logger.info('[Chat] 📊 Streaming complete, parsing tags from full response');
        logger.info('[Chat] 🔍 Accumulated content length:', accumulatedContent.length);
        logger.info('[Chat] 🔍 Looking for trade tags in content:', accumulatedContent.substring(accumulatedContent.length - 500));

        const allRecommendations: Array<{
          asset: string;
          deeplink: string;
          price?: string;
        }> = [];

        // Parse trade tags from complete accumulated content
        // Handles multiple formats (agent output is non-deterministic):
        // - Self-closing: <trade asset="BTC-USD"/>
        // - With price: <trade asset="BTC-USD" price="419.40"/>
        const recommendationRegex = /<trade\s+asset="([^"]+)"(?:\s+price="([^"]+)")?\s*\/?>/g;
        const matches = Array.from(accumulatedContent.matchAll(recommendationRegex));
        logger.info('[Chat] 🔍 Regex matches found:', matches.length);

        if (matches.length > 0) {
          logger.info(
            '[Chat] ✅ Found trade tags:',
            matches.map((m) => m[0]),
          );

          for (const match of matches) {
            const [fullMatch, asset, price] = match;
            const mappedAsset = mapToVantageSymbol(asset);
            logger.info('[Chat] 📦 Processing trade tag:', { fullMatch, asset, mappedAsset, price });

            // Generate deeplink client-side using Vantage asset utilities
            const deeplink = generateVantageDeeplink(mappedAsset);

            if (deeplink) {
              logger.info('[Chat] ✓ Trade recommendation created:', { asset: mappedAsset, deeplink });
              allRecommendations.push({ asset: mappedAsset, deeplink, price });
            } else {
              logger.info('[Chat] ✗ Asset not available on Vantage, skipping button:', asset);
            }
          }
        }

        // Parse followup queries
        const { queries: followupQueries } = parseFollowupQueries(accumulatedContent);

        // Remove trade tags from display content
        let cleanedContent = accumulatedContent;
        cleanedContent = cleanedContent.replace(/<trade\s+asset="[^"]+"\s+price="[^"]+"\s*\/>/g, ''); // With price (quoted)
        cleanedContent = cleanedContent.replace(/<trade\s+asset="[^"]+"\s*\/>/g, ''); // Without price

        logger.info('[Chat] 🔍 Setting message with:', {
          tradeRecommendations: allRecommendations,
          followupQueries,
          isStreaming: false,
        });

        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === assistantMessageId
              ? {
                  ...msg,
                  content: cleanedContent,
                  isStreaming: false,
                  hasBeenParsed: true,
                  traceId: traceId || null,
                  tradeRecommendations: allRecommendations.length > 0 ? allRecommendations : undefined,
                  followupQueries: followupQueries,
                }
              : msg,
          ),
        );

        // Add session to sidebar after first message completes successfully
        if (isCreatingNewSession) {
          const isNotificationSession = !!currentNotificationId;
          addSession({
            session_id: sessionIdToUse,
            created_at: new Date().toISOString(),
            session_title: firstUserMessage.slice(0, 50), // First 50 chars
            source: isNotificationSession ? 'notification' : undefined,
          });

          // Link notification to the new session so return visits navigate directly
          if (currentNotificationId) {
            linkSessionToNotification(currentNotificationId, sessionIdToUse);
          }
        }
      }
    } catch (error) {
      if (error instanceof SessionLimitReachedError) {
        setInput(messageContent);
        setMessages((prev) => prev.filter((message) => message.id !== userMessage.id && message.id !== assistantMessageId));
        return;
      }

      // Check if error is due to abort (either timeout or user cancellation)
      const isAbort = error instanceof Error && error.name === 'AbortError';

      let errorMessage: string;
      if (isAbort && userCancelledRef.current) {
        // User clicked stop button - this is expected, not an error
        logger.info('[Chat] Stream cancelled by user');
        errorMessage = `_${t('chat.responseStopped')}_`;
      } else if (isAbort) {
        // Timeout (180 seconds elapsed)
        logger.warn('[Chat] Stream timed out after 180 seconds');
        errorMessage = 'The request took too long to complete. Please try again or rephrase your query.';
      } else {
        // Other errors (network, server, etc.)
        logger.error('[Chat] Error sending message:', error);
        // If error already has a user-friendly message (from structured error handling), use it
        // Otherwise, use generic fallback
        errorMessage = error instanceof Error && error.message ? error.message : 'Sorry, there was an error connecting to the server.';
      }

      setMessages((prev) =>
        prev.map((msg) => {
          if (msg.id !== assistantMessageId) {
            return msg;
          }

          if (userCancelledRef.current && msg.content?.includes(t('chat.responseStopped'))) {
            return { ...msg, isStreaming: false, hasBeenParsed: true };
          }

          return {
            ...msg,
            content: msg.content ? msg.content + '\n\n' + errorMessage : errorMessage,
            isStreaming: false,
            hasBeenParsed: true,
          };
        }),
      );
    } finally {
      if (timeoutId) {
        clearTimeout(timeoutId); // Always clear timeout
      }
      if (phaseTimeoutId) {
        clearTimeout(phaseTimeoutId); // Clear phase timeout
      }
      setIsLoading(false);
      setAbortController(null); // Clear the abort controller
      setCurrentStreamId(null); // Clear the stream ID
    }
  };

  const stopStreaming = async () => {
    if (!abortController) return;

    logger.info('[Chat] Stop button clicked, cancelling stream...');

    try {
      // Set flag to indicate user cancellation
      userCancelledRef.current = true;

      // Abort the current fetch request (this is the critical step)
      abortController.abort();
      logger.info('[Chat] ✓ Frontend stream aborted successfully');

      // Optimistically stop streaming in UI so users can send a new message immediately
      setIsLoading(false);
      setAbortController(null);
      setCurrentStreamId(null);
      setMessages((prev) => {
        if (prev.length === 0) return prev;
        const lastIndex = prev.length - 1;
        const lastMessage = prev[lastIndex];
        if (lastMessage.role !== 'assistant' || !lastMessage.isStreaming) {
          return prev;
        }
        const cancellationText = `_${t('chat.responseStopped')}_`;
        const content = lastMessage.content?.includes(cancellationText)
          ? lastMessage.content
          : lastMessage.content
            ? lastMessage.content + '\n\n' + cancellationText
            : cancellationText;
        const updated = {
          ...lastMessage,
          content,
          isStreaming: false,
          hasBeenParsed: true,
        };
        return [...prev.slice(0, lastIndex), updated];
      });

      // Send cancellation request to backend (best-effort cleanup)
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };

      // Include stream_id in cancel request if available
      const cancelBody: { session_id: string; stream_id?: string } = {
        session_id: sessionId,
      };

      if (currentStreamId) {
        cancelBody.stream_id = currentStreamId;
      } else {
        logger.warn('[Chat] No stream ID available - skipping backend cancel (frontend abort only)');
        return;
      }

      const response = await fetch(`/api/cancel`, {
        method: 'POST',
        headers,
        body: JSON.stringify(cancelBody),
      });

      if (response.ok) {
        logger.info('[Chat] ✓ Backend cancellation confirmed');
      } else if (response.status === 500) {
        logger.warn('[Chat] Backend cancellation failed with 500 - unexpected server error');
      } else {
        logger.warn('[Chat] Backend cancellation failed with status:', response.status);
      }
    } catch (error) {
      logger.warn('[Chat] Backend cancellation request failed:', error);
      // Don't show error to user - cancellation already happened on frontend
    }
  };

  const handleQuestionClick = (fullQuery: string) => {
    sendMessage(fullQuery);
  };

  const handleFeedbackClick = async (messageId: string, feedback: 'positive' | 'negative') => {
    const message = messages.find((m) => m.id === messageId);
    if (!message) return;

    setMessages((prev) => prev.map((msg) => (msg.id === messageId ? { ...msg, feedbackStatus: feedback } : msg)));

    if (!message.traceId) {
      logger.info('Feedback button clicked but no trace ID available');
      return;
    }

    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };

      const response = await fetch('/api/feedback', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          traceId: message.traceId,
          feedback,
          comment: '',
        }),
      });

      if (!response.ok) {
        // Handle authentication errors by redirecting to login
        if (response.status === 401) {
          logger.error('[Chat] Authentication failed during feedback - redirecting to login');
          // window.location.href = '/api/auth/login';
          return;
        }
        logger.error('Feedback submission failed:', response.status);
      }
    } catch (error) {
      logger.error('Feedback API error:', error);
    }
  };

  const handleFollowupClick = (messageId: string, query: string) => {
    // Hide followups for this message
    setMessages((prev) => prev.map((msg) => (msg.id === messageId ? { ...msg, followupsHidden: true } : msg)));

    // Send the followup query
    sendMessage(query);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        sendMessage();
      } else if (!e.shiftKey) {
        e.preventDefault();
        sendMessage();
      }
    }
  };

  const adjustTextareaHeight = useCallback(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      const newHeight = Math.min(textarea.scrollHeight, 120);
      textarea.style.height = `${newHeight}px`;
    }
  }, []);

  useEffect(() => {
    adjustTextareaHeight();
  }, [input, adjustTextareaHeight]);

  useEffect(() => {
    textareaRef.current?.focus();
  }, []);

  const focusTextarea = useCallback(() => {
    setTimeout(() => textareaRef.current?.focus(), 100);
  }, []);

  const checkForProgressiveParsing = useCallback((messageId: string, content: string) => {
    if (hasCompleteTables(content)) {
      setMessages((prev) => prev.map((msg) => (msg.id === messageId && !msg.hasBeenParsed ? { ...msg, hasBeenParsed: true } : msg)));
    }
  }, []);

  // const handlePersonaChange = (newPersona: PersonaType) => {
  //   setPersona(newPersona);
  //   setSelectedPersona(newPersona); // Save to localStorage
  // };

  // Check if currently streaming (last message has isStreaming flag)
  const isStreaming = messages.length > 0 && messages[messages.length - 1]?.isStreaming;

  return (
    // 右边聊天区域
    <>
      <div className="flex min-h-[100dvh] flex-col bg-[#070400]">
        <Header />
        {/* <OnboardingDialog
        open={showOnboarding}
        onComplete={() => setShowOnboarding(false)}
        onSkip={() => {
          localStorage.setItem(`onboarding_dismissed_${_user?.sub}`, 'true');
          setShowOnboarding(false);
        }}
      /> */}

        {/* Main Content */}
        <main className="relative flex flex-1 flex-col pt-16">
          <div className="relative mx-auto flex w-full max-w-4xl min-w-0 flex-1 flex-col px-3 sm:px-6 lg:px-8">
            {/* Jump to Bottom Button - Fixed positioning with sidebar-aware centering */}
            {messages.length > 0 && showScrollButton && (
              <button
                onClick={() => scrollToBottom()}
                className={`fixed bottom-24 left-1/2 z-50 -translate-x-1/2 cursor-pointer rounded-full border border-white/16 bg-[#313131]/60 p-3 shadow-lg transition-all duration-300 sm:bottom-32 ${
                  isRTL ? (isSidebarOpen ? 'md:mr-32' : 'md:mr-0') : isSidebarOpen ? 'md:ml-32' : 'md:ml-0'
                }`}
                aria-label={t('chat.jumpToBottom')}
                title={t('chat.jumpToBottom')}>
                <ArrowDown className="w-5 h-5  text-white" />
              </button>
            )}
            {/* Messages Area */}
            <div className="flex-1 space-y-4 py-4 pr-0 sm:space-y-6 sm:py-8 sm:pr-6">
              {/* <AssetChart
              symbol={'BTC-USD'}
              assetType={'crypto'}
              interval={'1d'}
              chartType={'candlestick'}
              start={'2026-05-16'}
              end={'2026-06-16'}
              studies={'RSI,MACD,BB'}
            /> */}
              {isLoadingSession ? (
                /* Loading State - prevents empty state flash when opening existing session */
                <div className="flex min-h-[calc(100dvh-8rem)] flex-col items-center justify-center py-6">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#e45728]"></div>
                  <p className="text-white mt-4">Loading conversation...</p>
                </div>
              ) : messages.length === 0 ? (
                /* Empty State - only for new chats */
                <div className="flex min-h-[calc(100dvh-8rem)] flex-col items-center justify-center py-6">
                  <div className="mb-6 text-center sm:mb-8">
                    <h1
                      translate="no"
                      className="notranslate mb-6 text-3xl font-normal text-[#ffffff] sm:mb-12 sm:text-5xl">
                      {t('chat.welcome')}
                    </h1>
                  </div>

                  {/* Input Section for Empty State */}
                  <div className="mb-5 w-full max-w-3xl sm:mb-8">
                    <div className="relative rounded-2xl border border-[rgba(255,255,255,0.1)] bg-[#231f20] p-3 shadow-[0_4px_24px_-4px_rgba(3,72,84,0.1)] sm:rounded-3xl sm:p-4">
                      <textarea
                        ref={textareaRef}
                        value={input}
                        onChange={(e) => {
                          if (e.target.value.length <= MAX_CHARS) {
                            setInput(e.target.value);
                          }
                        }}
                        onKeyPress={handleKeyPress}
                        placeholder={t('chat.placeholder')}
                        disabled={isLoading}
                        rows={2}
                        className="w-full pr-14 py-2 px-2 border-0 outline-none resize-none bg-transparent placeholder-[#666] text-white text-base cursor-text"
                        style={{
                          minHeight: '40px',
                          maxHeight: '120px',
                          overflowY: input.split('\n').length > 6 ? 'auto' : 'hidden',
                        }}
                      />

                      {/* Agent and Persona Selectors */}
                      <div className="flex flex-wrap items-center justify-between gap-1 border-t border-[rgba(255,255,255,0.04)] pt-3 sm:gap-2">
                        <div className="flex min-w-0 shrink-0 flex-nowrap items-center gap-1 sm:flex-1 sm:gap-3">
                          <AgentSelector
                            selectedAgent={selectedAgent}
                            onAgentChange={setSelectedAgent}
                            restrictions={agentRestrictions}
                            onRestrictedAgentClick={handleRestrictedAgentClick}
                          />
                          <PersonaSelector />
                        </div>

                        <div className="ml-auto flex min-w-0 shrink-0 items-center gap-1 sm:gap-2">
                          <MtAccountSelector
                            accounts={mtAccounts}
                            value={activeMtAccount}
                            onValueChange={setSelectedMtAccount}
                            onUnavailableClick={handleUnavailableMtAccountClick}
                            disabled={isLoading || authLoading}
                          />

                          {/* Send/Stop Button */}
                          <button
                            onClick={isStreaming ? stopStreaming : () => sendMessage()}
                            disabled={!isStreaming && (isLoading || !input.trim() || input.length > MAX_CHARS)}
                            className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-white/8 bg-white/6 opacity-100 transition-all duration-200 disabled:cursor-not-allowed not-disabled:cursor-pointer not-disabled:bg-[#f98715]`}
                            aria-label={isStreaming ? t('chat.stopGenerating') : t('chat.sendMessage')}
                            title={isStreaming ? t('chat.stopGenerating') : t('chat.sendMessage')}>
                            {isStreaming ? (
                              <svg
                                width="20"
                                height="20"
                                viewBox="0 0 24 24"
                                fill="white">
                                <rect
                                  x="6"
                                  y="6"
                                  width="12"
                                  height="12"
                                />
                              </svg>
                            ) : (
                              <Send
                                width="20"
                                className="text-white"
                                height="20"
                              />
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                    <div className={`mt-1 flex ${isRTL ? 'justify-start' : 'justify-end'} text-xs text-white/60`}>
                      {/* 9999 代表无限次使用 */}
                      {remainingDay === 9999 ? (
                        <span>
                          {t('chat.limit')} <span className="text-[#f98715]">{t('unlimited')}</span>
                        </span>
                      ) : (
                        <span>
                          {t('chat.limit')} <span className="text-[#f98715]">{remaining}</span>/<span>{remainingDay}</span>
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Recommended Questions */}
                  <RecommendedQuestions
                    key={questionsKey}
                    onQuestionClick={handleQuestionClick}
                  />
                </div>
              ) : (
                /* Messages */
                <div className="space-y-5 sm:space-y-6">
                  {messages.map((message) => (
                    <div key={message.id}>
                      {message.role === 'user' ? (
                        <div className="group flex justify-end">
                          <div className="mr-2 mt-2 hidden self-start opacity-0 transition-opacity group-hover:opacity-100 sm:block">
                            <CopyButton
                              text={message.content}
                              className="bg-white/8 border border-white/16 text-[#ccc]"
                            />
                          </div>
                          <div className="max-w-[88%] break-words rounded-2xl rounded-tr-lg bg-[#231F20] px-3 py-2 text-white/90 sm:max-w-[75%] sm:px-4">
                            <MessageContent
                              content={message.content}
                              role={message.role}
                              klineChartEnabled={userAccess.features.kline_chart}
                              klineRestriction={klineRestriction ?? undefined}
                              onLockedKlineClick={handleLockedKlineClick}
                              tradeRecommendations={message.tradeRecommendations}
                            />
                          </div>
                        </div>
                      ) : (
                        <div className="w-full">
                          <div className="prose prose-sm max-w-none break-words sm:prose">
                            {message.isStreaming && (
                              <div className={message.content ? 'mb-3' : ''}>
                                <AgentStatusIndicator
                                  status={streamingPhase}
                                  label={streamingLabel || t(`chat.status.${streamingPhase}`)}
                                  compact={Boolean(message.content)}
                                />
                              </div>
                            )}
                            <MessageContent
                              content={message.content}
                              isStreaming={message.isStreaming}
                              role={message.role}
                              hasBeenParsed={message.hasBeenParsed}
                              klineChartEnabled={userAccess.features.kline_chart}
                              klineRestriction={klineRestriction ?? undefined}
                              onLockedKlineClick={handleLockedKlineClick}
                              tradeRecommendations={message.tradeRecommendations}
                            />
                            {message.isStreaming && message.content && <span className="ml-1 inline-block h-4 w-2 rounded-[2px] bg-[#f98715] motion-safe:animate-pulse" />}
                          </div>

                          {!message.isStreaming && <div className="mt-3 text-xs text-gray-400 italic">{t('chat.disclaimer')}</div>}

                          {!message.isStreaming && message.followupQueries && (
                            <FollowupQueries
                              queries={message.followupQueries}
                              onQueryClick={(query) => handleFollowupClick(message.id, query)}
                              disabled={isLoading}
                              hidden={message.followupsHidden || false}
                            />
                          )}

                          {!message.isStreaming && message.content !== 'Sorry, there was an error connecting to the server.' && (
                            <FeedbackButtons
                              traceId={message.traceId || null}
                              feedbackStatus={message.feedbackStatus || null}
                              onFeedbackClick={(feedback) => handleFeedbackClick(message.id, feedback)}
                              contentToCopy={message.content}
                            />
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                  {isLoading && messages.length > 0 && !messages[messages.length - 1]?.isStreaming && (
                    <div className="w-full flex items-center text-white">
                      <span>Connecting</span>
                      <span className="thinking-dots ml-1">
                        <span>.</span>
                        <span>.</span>
                        <span>.</span>
                      </span>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </div>
              )}
            </div>

            {/* Chat Input (when messages exist) - Sticky at bottom */}
            {messages.length > 0 && (
              <div className="sticky bottom-0 z-10 -mx-3 border-t border-[rgba(255,255,255,0.04)] bg-[#070400] px-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] pt-3 sm:mx-0 sm:px-0 sm:py-6">
                <div className="relative rounded-2xl border border-[rgba(255,255,255,0.1)] bg-[#231f20] p-3 shadow-sm">
                  <textarea
                    ref={textareaRef}
                    value={input}
                    onChange={(e) => {
                      if (e.target.value.length <= MAX_CHARS) {
                        setInput(e.target.value);
                      }
                    }}
                    onKeyPress={handleKeyPress}
                    placeholder={t('chat.placeholder')}
                    disabled={isLoading}
                    rows={1}
                    className="w-full resize-none border-0 bg-transparent px-3 py-2 text-base text-white outline-none placeholder-[#666] cursor-text"
                    style={{
                      minHeight: '40px',
                      maxHeight: '120px',
                      overflowY: input.split('\n').length > 6 ? 'auto' : 'hidden',
                    }}
                  />

                  <div className="flex items-center justify-end gap-1 border-t border-white/[0.04] pt-2 sm:gap-2">
                    <MtAccountSelector
                      accounts={mtAccounts}
                      value={activeMtAccount}
                      onValueChange={setSelectedMtAccount}
                      onUnavailableClick={handleUnavailableMtAccountClick}
                      disabled={isLoading || authLoading}
                    />
                    <button
                      onClick={isStreaming ? stopStreaming : () => sendMessage()}
                      disabled={!isStreaming && (isLoading || !input.trim() || input.length > MAX_CHARS)}
                      className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-white/8 bg-white/6 opacity-100 transition-all duration-200 cursor-pointer disabled:cursor-not-allowed not-disabled:bg-[#f98715]`}
                      aria-label={isStreaming ? t('chat.stopGenerating') : t('chat.sendMessage')}
                      title={isStreaming ? t('chat.stopGenerating') : t('chat.sendMessage')}>
                      {isStreaming ? (
                        <svg
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          fill="white">
                          <rect
                            x="6"
                            y="6"
                            width="12"
                            height="12"
                          />
                        </svg>
                      ) : (
                        <Send
                          width="20"
                          className="text-white"
                          height="20"
                        />
                      )}
                    </button>
                  </div>
                </div>
                <div className={`mt-1 flex ${isRTL ? 'justify-start' : 'justify-end'} text-xs text-white/60`}>
                  {/* 9999 代表无限次使用 */}
                  {remainingDay === 9999 ? (
                    <span>
                      {t('chat.limit')} <span className="text-[#f98715]">Unlimited</span>
                    </span>
                  ) : (
                    <span>
                      {t('chat.limit')} <span className="text-[#f98715]">{remaining}</span>/<span>{remainingDay}</span>
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
      <SessionAccessDialog
        open={sessionDialog.open}
        onOpenChange={(open) => setSessionDialog((prev) => ({ ...prev, open }))}
        variant={sessionDialog.variant}
        language={language}
        level={userLevel}
        limit={sessionDialog.limit}
      />
      <FeatureAccessDialog
        open={chatUsageDialogOpen}
        onOpenChange={setChatUsageDialogOpen}
        restriction="upgrade"
        feature="chat-usage"
      />
      <FeatureAccessDialog
        open={recommendationDialog.open}
        onOpenChange={(open) => setRecommendationDialog((prev) => ({ ...prev, open }))}
        restriction={recommendationDialog.restriction}
        feature="recommendation"
      />
      <FeatureAccessDialog
        open={featureDialog.open}
        onOpenChange={setFeatureDialogOpen}
        restriction={featureDialog.restriction}
      />
      <FeatureAccessDialog
        open={klineDialogOpen}
        onOpenChange={setKlineDialogOpen}
        restriction={klineRestriction ?? 'upgrade'}
        feature="kline-chart"
      />
      <MembershipTiersDialog
        open={membershipDialogOpen}
        onOpenChange={setMembershipDialogOpen}
      />
    </>
  );
}
